import type { FC, ReactNode } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { Moon, Sparkles } from 'lucide-react'
import { pad, useCountdown } from '@/hooks/use-countdown'
import { useSiteContent } from '@/lib/site-content'
import CmsText from '@/components/generals/cms-text'
import CountdownDigit from '@/components/motion/countdown-digit'
import { FadeIn } from '@/components/motion/fade-in'

const Countdown: FC = (): ReactNode => {
    const { settings } = useSiteContent()
    const { days, hours, minutes, seconds, done } = useCountdown(settings.eid_date ?? '')

    return (
        <section
            id='countdown'
            className='relative bg-brand-red text-white'
            aria-labelledby='countdown-title'
        >
            <div className='container-page section-pad'>
                <AnimatePresence mode='wait' initial={false}>
                    {done ? (
                        <motion.div
                            key='celebrate'
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4 }}
                            className='flex flex-col items-center gap-4 text-center'
                        >
                            <Moon aria-hidden className='h-10 w-10 text-brand-yellow' />

                            <h2
                                id='countdown-title'
                                className='font-display text-4xl font-extrabold md:text-6xl'
                                data-mdlfy-cms={`site_settings:${settings.id}:celebration_message`}
                            >
                                {settings.celebration_message}
                            </h2>

                            <p
                                className='max-w-xl text-base text-white/85 md:text-lg'
                                data-mdlfy-cms={`site_settings:${settings.id}:celebration_subtitle`}
                            >
                                {settings.celebration_subtitle}
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key='counting'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className='grid grid-cols-1 items-center gap-8 md:grid-cols-12 md:gap-10'
                        >
                            <FadeIn className='md:col-span-4'>
                                <div className='flex items-center gap-3 text-brand-yellow'>
                                    <Sparkles aria-hidden className='h-5 w-5' />

                                    <span
                                        className='text-sm font-semibold uppercase tracking-[0.2em]'
                                        data-mdlfy-cms={`site_settings:${settings.id}:eid_name`}
                                    >
                                        {settings.eid_name}
                                    </span>
                                </div>

                                <CmsText
                                    as='h2'
                                    id='countdown-title'
                                    textKey='countdown.title'
                                    className='mt-3 font-display text-4xl font-extrabold leading-tight md:text-5xl'
                                />
                            </FadeIn>

                            <div className='md:col-span-8'>
                                <div className='grid grid-cols-4 gap-3 md:gap-5'>
                                    <Unit labelKey='countdown.units.days' value={pad(days)} />
                                    <Unit labelKey='countdown.units.hours' value={pad(hours)} />
                                    <Unit labelKey='countdown.units.minutes' value={pad(minutes)} />
                                    <Unit labelKey='countdown.units.seconds' value={pad(seconds)} subtle />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    )
}

interface UnitProps {
    value: string
    labelKey: string
    subtle?: boolean
}

const Unit: FC<UnitProps> = ({ value, labelKey, subtle }): ReactNode => {
    return (
        <div className='text-center'>
            <div className='mx-auto inline-flex font-display text-5xl font-extrabold tabular-nums text-white md:text-7xl'>
                <CountdownDigit value={value} />
            </div>

            <CmsText
                as='div'
                textKey={labelKey}
                className={'mt-2 text-xs font-semibold uppercase tracking-[0.2em] ' + (subtle ? 'text-white/55' : 'text-white/80')}
            />
        </div>
    )
}

export default Countdown
